// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import authAction from 'app/common/react/components/limitedLogin/authAction';
import BoardCreateModal from 'app/common/react/components/profile/ProfileBoards/BoardCreateModal';
import ContentHeader from 'app/common/react/ui/ContentHeader';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import MessageButton from 'app/common/react/components/profile/ProfileHeader/MessageButton';
import ProfileActionsButton from 'app/common/react/components/profile/ProfileActionsButton/ProfileActionsButton';
import SendProfileButtonController from 'app/common/react/components/sending/SendProfileButtonController';
import type { ProfileBoard } from 'app/packages/pinterest-flow-types/board';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Router } from 'app/common/lib/ReactRouter/types';
import UserFollowButton from 'app/common/react/components/FeedsAndFollowing/FollowButton/UserFollowButton';
import { Experiment } from 'app/common/react/components/experiments';
import { resourceInvalidate } from 'app/common/redux/actions/resourceFetch';
import { withRouter } from 'react-router';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import { Box, Button, Column, Container, IconButton, Text } from 'gestalt';
import { compose, type Dispatch } from 'redux';

type HOCProps = {|
  router: Router,
  viewer: Viewer,
|};

type DispatchProps = {|
  invalidatePinBetterSaveResource: () => void,
|};

type OwnProps = {|
  isOwnProfile: boolean,
  onToggleBlock: () => void,
  onToggleFollow: () => void,
  showFixedHeaderName: ?boolean,
  user: ProfileUser,
  ...HOCProps,
|};

type Props = {| ...OwnProps, ...DispatchProps |};

type State = {|
  isCreateModalOpen: boolean,
|};

export class FixedHeader extends React.Component<Props, State> {
  state = {
    isCreateModalOpen: false,
  };

  navigateToSettings = () => {
    this.props.router.push('/settings#profile');
  };

  handlePinCreate = () => {
    const { router } = this.props;
    router.push('/pin-builder/');
  };

  handlePinCollageCreate = () => {
    const { router } = this.props;
    router.push('/pin-collage/');
  };

  handleShowBoardCreate = authAction(() => {
    this.setState({ isCreateModalOpen: true });
  });

  handleBoardCreate = (board: ProfileBoard) => {
    const { invalidatePinBetterSaveResource, router } = this.props;
    invalidatePinBetterSaveResource();
    router.push({
      pathname: board.url,
      state: {
        newBoard: true,
      },
    });
  };

  handleHide = () => {
    this.setState({ isCreateModalOpen: false });
  };

  header: ?HTMLElement = null;

  render() {
    const {
      isOwnProfile,
      onToggleBlock,
      onToggleFollow,
      showFixedHeaderName,
      user,
      user: {
        blocked_by_me,
        explicitly_followed_by_me,
        username,
        full_name: fullName,
        id: userId,
        image_large_url: imageUrl = '',
      },
      viewer,
    } = this.props;

    const { isCreateModalOpen } = this.state;

    const profileUrl = `/${username}/`;

    const shouldShowBlockButton = !isOwnProfile && !user.blocked_by_me;

    const actionButtons = (
      <React.Fragment>
        {viewer.isAuth && (
          <Box display="flex" direction="row" alignItems="center" flex="none">
            {isOwnProfile && (
              <React.Fragment>
                <ProfileActionsButton
                  onCreateBoard={this.handleShowBoardCreate}
                  onCreatePin={this.handlePinCreate}
                  onCreatePinCollage={this.handlePinCollageCreate}
                />
                <IconButton
                  accessibilityLabel={i18n._('Settings', 'Icon to go to the settings page')}
                  icon="edit"
                  onClick={this.navigateToSettings}
                  size="lg"
                />
              </React.Fragment>
            )}
            <SendProfileButtonController user={user} />
            {shouldShowBlockButton && (
              <IconButton
                accessibilityLabel={i18n._('Block', 'label for block button')}
                icon="flag"
                onClick={onToggleBlock}
                size="lg"
              />
            )}
          </Box>
        )}
      </React.Fragment>
    );

    const displayName = (
      <Box
        alignItems="center"
        display="none"
        smDisplay="flex"
        padding={2}
        justifyContent="center"
        dangerouslySetInlineStyle={{
          __style: { flex: '1 1 0' },
        }}
        overflow="hidden"
      >
        {viewer.isAuth && (
          <div
            className={cx('boardName', {
              transitionIn: showFixedHeaderName,
              transitionOut: showFixedHeaderName === false,
            })}
          >
            <Text align="center" bold truncate size="lg">
              {user.full_name}
            </Text>
          </div>
        )}
      </Box>
    );

    const rightButtons = (
      <React.Fragment>
        {viewer.isAuth &&
          !isOwnProfile && (
            <Box display="flex" justifyContent="end" alignSelf="end" flex="none">
              <Box display="flex">
                {!blocked_by_me ? (
                  <Box paddingX={2}>
                    <MessageButton userId={userId} />
                  </Box>
                ) : null}
                {blocked_by_me ? (
                  <Button
                    color="gray"
                    inline
                    onClick={onToggleBlock}
                    size="md"
                    text={i18n._('Unblock')}
                  />
                ) : (
                  <UserFollowButton
                    fullName={fullName}
                    hideFollowingText
                    id={userId}
                    imageUrl={imageUrl}
                    inline
                    isOwnProfile={isOwnProfile}
                    onUserFollow={onToggleFollow}
                    profileUrl={profileUrl}
                    userFollowed={explicitly_followed_by_me}
                  />
                )}
              </Box>
            </Box>
          )}
      </React.Fragment>
    );
    return (
      <React.Fragment>
        <ContentHeader
          className={viewer.isAuth ? 'fixedHeader' : null}
          position={viewer.isAuth ? 'fixed' : undefined}
        >
          <Container>
            <Box
              alignItems="center"
              display="flex"
              marginTop={1}
              paddingY={2}
              width="100%"
              marginBottom={1}
              justifyContent={'between'}
            >
              {isOwnProfile ? <Column span={4}>{actionButtons}</Column> : actionButtons}
              {isOwnProfile ? <Column span={4}>{displayName}</Column> : displayName}
              {isOwnProfile ? <Column span={4}>{rightButtons}</Column> : rightButtons}
            </Box>
          </Container>
        </ContentHeader>
        {isCreateModalOpen && (
          // Activates the user into the experiment.  BoardPage handles displaying the different group treatments
          <Experiment name="web_curation_more_ideas_jumpstart_board_v2">
            {() => (
              <BoardCreateModal onBoardCreate={this.handleBoardCreate} onClose={this.handleHide} />
            )}
          </Experiment>
        )}
      </React.Fragment>
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
  return {
    invalidatePinBetterSaveResource: () =>
      dispatch(resourceInvalidate('BoardPickerBoardsResource', { field_set_key: 'board_picker' })),
  };
}

export default compose(
  withViewer,
  withRouter,
  connect<Props, OwnProps, _, _, _, _>(
    null,
    mapDispatchToProps,
  ),
)(FixedHeader);
